import { useRecoilCallback, useRecoilValue } from 'recoil';
import type { HangingProtocol } from './useHangingProtocol';
import {
  confirmedHangingProtocolSelector,
  isPreviewingHangingProtocolState,
  previewHangingProtocolSelector,
  previousHangingProtocolState,
} from './state';
import { useCurrentCaseId } from 'hooks/useCurrentCase';
import { client } from 'modules/Apollo/client';
import { GET_HANGING_PROTOCOL } from 'modules/Apollo/queries';
import { useState } from 'react';
import { usePreviousHangingProtocol } from './usePreviousHangingProtocol';
import { useExamGroupedPriorStudies, usePriorStudyGroups } from './useStudiesGroupedAsCases';
import { FF, useFeatureFlagEnabled } from 'modules/feature-flags/suspense';
import { WithApolloContext } from 'modules/Apollo/types';
import { logger } from 'modules/logger';

export type PreviewHangingProtocol = (id: string) => Promise<void>;

export type UsePreviewHangingProtocol = {
  previewHangingProtocol: PreviewHangingProtocol;
  confirmPreview: () => void;
  confirmCreatedHangingProtocol: (id: string) => Promise<void>;
  clearPreviewState: () => void;
  rejectAndRemovePreview: () => void;
  clearConfirmedHangingProtocol: () => void;
  loadingPreview: boolean;
};

export const usePreviewHangingProtocolConfiguration = (): Nullable<HangingProtocol> => {
  const currentCaseSmid = useCurrentCaseId();
  return useRecoilValue(previewHangingProtocolSelector(currentCaseSmid));
};

export const useConfirmedHangingProtocolConfiguration = (): Nullable<HangingProtocol> => {
  const currentCaseSmid = useCurrentCaseId();
  return useRecoilValue(confirmedHangingProtocolSelector(currentCaseSmid));
};

export const usePreviewHangingProtocol = ({
  context,
}: WithApolloContext): UsePreviewHangingProtocol => {
  const enableExamGrouping = useFeatureFlagEnabled(FF.EXAM_GROUPED_HANGING_PROTOCOLS);
  const studiesGroupedByExam = useExamGroupedPriorStudies({ context });
  const ungroupedStudies = usePriorStudyGroups({ context });
  const cases = enableExamGrouping ? studiesGroupedByExam : ungroupedStudies;
  const { savePreviousHangingProtocol } = usePreviousHangingProtocol({
    context,
  });

  const [loadingPreview, setLoadingPreview] = useState(false);

  const currentCaseSmid = useCurrentCaseId();

  const previewHangingProtocol = useRecoilCallback(
    ({ set, snapshot }) => {
      return async (id: string) => {
        if (currentCaseSmid == null || cases.length === 0) return;
        setLoadingPreview(true);
        const isAlreadyPreviewing = snapshot
          .getLoadable(isPreviewingHangingProtocolState)
          .getValue();
        if (!isAlreadyPreviewing) {
          savePreviousHangingProtocol();
          set(isPreviewingHangingProtocolState, true);
        }

        try {
          const { data } = await client.query({
            query: GET_HANGING_PROTOCOL,
            variables: { id, cases },
            context: {
              ...context,
              telemetry: {
                ...(context.telemetry ?? {}),
                fetchPolicy: 'no-cache',
              },
            },
            // GET_HANGING_PROTOCOL resolves viewport study/series/stack references against
            // the *current case*, but HangingProtocol normalizes by smid in the shared
            // cache — writing preview results into the cache would overwrite the canonical
            // (non-case-resolved) entity that the HP list table reads from, making the
            // previewed HP appear mutated there. no-cache skips both the cache read and
            // write for this query, keeping preview fetches fully isolated.
            fetchPolicy: 'no-cache',
          });
          const hangingProtocol = data?.hangingProtocol;

          logger.info(`Previewed Hanging Protocol ${hangingProtocol?.smid}`);

          set(previewHangingProtocolSelector(currentCaseSmid), hangingProtocol);
          setLoadingPreview(false);
        } catch (error: any) {
          logger.error('Failed to fetch hanging protocol', error);
          setLoadingPreview(false);
        }
      };
    },
    [cases, context, currentCaseSmid, savePreviousHangingProtocol]
  );

  // Pins a just-created HP as the confirmed one for the current case. The server's
  // matchHangingProtocol can tie a brand-new HP with an existing HP that has identical
  // study/classification criteria and keep returning the OLDER one on the post-create refetch, so
  // the newly-created HP would otherwise never become active -- from the user's perspective,
  // creating a second HP silently "does nothing" (VX-4713). Writing directly to the confirmed
  // atom (which wins over matched in useHangingProtocol's originalHangingProtocol chain) makes the
  // new HP active immediately. Unlike previewHangingProtocol, this deliberately does NOT touch
  // isPreviewingHangingProtocolState / previousHangingProtocolState -- there is no preview to
  // accept or roll back, the HP is already durable on the backend.
  //
  // Fetches with `cases` (like previewHangingProtocol) so viewport study/series/stack references
  // resolve against the current case. A fetch WITHOUT cases returns under-resolved viewports and
  // paints "series incompatible" errors in the grid. no-cache for the same reason preview uses it:
  // GET_HANGING_PROTOCOL resolves against the current case, and writing that into the shared
  // normalized cache would corrupt the canonical (non-case-resolved) entity the HP list reads.
  const confirmCreatedHangingProtocol = useRecoilCallback(
    ({ set }) =>
      async (id: string) => {
        if (currentCaseSmid == null || cases.length === 0) return;
        try {
          const { data } = await client.query({
            query: GET_HANGING_PROTOCOL,
            variables: { id, cases },
            context: {
              ...context,
              telemetry: {
                ...(context.telemetry ?? {}),
                fetchPolicy: 'no-cache',
              },
            },
            fetchPolicy: 'no-cache',
          });
          const hangingProtocol = data?.hangingProtocol;
          if (hangingProtocol != null) {
            set(confirmedHangingProtocolSelector(currentCaseSmid), hangingProtocol);
          }
        } catch (error) {
          logger.error('Failed to confirm newly-created hanging protocol', error);
        }
      },
    [cases, context, currentCaseSmid]
  );

  // Discard-only cleanup: clears preview state without promoting it to confirmedHangingProtocolState.
  // Used when preview state needs to be reset as a side effect of an unrelated action (e.g.
  // submitting the HP create/update form), where promoting an unconfirmed preview would be wrong.
  const clearPreviewState = useRecoilCallback(
    ({ set, reset }) =>
      () => {
        if (currentCaseSmid == null) return;

        reset(previewHangingProtocolSelector(currentCaseSmid));
        set(isPreviewingHangingProtocolState, false);
        reset(previousHangingProtocolState);
      },
    [currentCaseSmid]
  );

  // Confirm: promote the in-memory preview to the durable, per-case confirmed atom, then delegate
  // to clearPreviewState for the shared cleanup. This must only run when the user explicitly
  // clicks Confirm — never as incidental cleanup (e.g. an unrelated HP form submit), since that
  // would silently make an unconfirmed preview durable.
  const confirmPreview = useRecoilCallback(
    ({ set, snapshot }) =>
      () => {
        if (currentCaseSmid == null) return;

        const previewedHangingProtocol = snapshot
          .getLoadable(previewHangingProtocolSelector(currentCaseSmid))
          .getValue();
        if (previewedHangingProtocol != null) {
          set(confirmedHangingProtocolSelector(currentCaseSmid), previewedHangingProtocol);
        }

        // viewport/layout resets are handled by resetToHP in useHangingProtocol
        clearPreviewState();
      },
    [currentCaseSmid, clearPreviewState]
  );

  // Reject: discard the preview entirely. The visible HP falls back to confirmedHangingProtocol ??
  // matchedHangingProtocol via useHangingProtocol's priority chain, which is exactly what
  // previousHangingProtocol snapshotted at preview-start time (see usePreviousHangingProtocol) — so
  // there is no need to write it back into the preview atom. Writing it back instead left a stale
  // value in previewHangingProtocolSelector that outlived the cancel and kept winning over later
  // clearConfirmedHangingProtocol() calls (e.g. Reset, delete) (VX-4531).
  const rejectAndRemovePreview = useRecoilCallback(
    ({ set, reset }) =>
      () => {
        if (currentCaseSmid == null) return;
        set(isPreviewingHangingProtocolState, false);
        reset(previewHangingProtocolSelector(currentCaseSmid));
        reset(previousHangingProtocolState);
      },
    [currentCaseSmid]
  );

  // Invalidates a previously confirmed HP for the current case — used when the case's HP is
  // reset to the server-matched default, or when the confirmed HP itself has been deleted.
  const clearConfirmedHangingProtocol = useRecoilCallback(
    ({ reset }) =>
      () => {
        if (currentCaseSmid == null) return;
        reset(confirmedHangingProtocolSelector(currentCaseSmid));
      },
    [currentCaseSmid]
  );

  return {
    previewHangingProtocol,
    confirmPreview,
    confirmCreatedHangingProtocol,
    clearPreviewState,
    rejectAndRemovePreview,
    clearConfirmedHangingProtocol,
    loadingPreview,
  };
};
